import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { logSessionExpired } from '../../services/auditService';

/**
 * SessionTimeout Component
 * Monitors user activity and automatically logs out after period of inactivity
 * Shows warning modal before logout
 *
 * Configuration:
 * - INACTIVITY_TIMEOUT: 30 minutes (1800000 ms)
 * - WARNING_TIME: 2 minutes before timeout (120000 ms)
 */

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_TIME = 2 * 60 * 1000; // 2 minutes warning before logout

const SessionTimeout = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetTimer = () => {
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Update last activity time
    lastActivityRef.current = Date.now();
    setShowWarning(false);

    // Only set timers if user is logged in
    if (!user) return;

    // Set warning timer (shows modal 2 minutes before logout)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
    }, INACTIVITY_TIMEOUT - WARNING_TIME);

    // Set logout timer
    timeoutRef.current = setTimeout(async () => {
      await handleLogout();
    }, INACTIVITY_TIMEOUT);
  };

  const handleLogout = async () => {
    try {
      // Log session expiration
      if (user) {
        await logSessionExpired(user.id, user.email, 'Inactivity timeout');
      }

      await signOut();
      setShowWarning(false);
      navigate('/login', {
        state: { message: 'Your session has expired due to inactivity. Please log in again.' }
      });
    } catch (error) {
      console.error('Error during timeout logout:', error);
    }
  };

  const handleStayLoggedIn = () => {
    setShowWarning(false);
    resetTimer();
  };

  useEffect(() => {
    // Only monitor activity if user is logged in
    if (!user) return;

    // Activity events to monitor
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Reset timer on any user activity
    const activityHandler = () => {
      resetTimer();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, activityHandler, true);
    });

    // Initialize timer
    resetTimer();

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, activityHandler, true);
      });

      // Clear timers
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [user]);

  // Don't render anything if not showing warning
  if (!showWarning) return null;

  // Render warning modal
  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-warning text-dark">
            <h5 className="modal-title">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Session Timeout Warning
            </h5>
          </div>
          <div className="modal-body">
            <p className="mb-0">
              Your session will expire in <strong>2 minutes</strong> due to inactivity.
              You will be automatically logged out for security purposes.
            </p>
            <p className="text-muted mt-2 mb-0">
              Click "Stay Logged In" to continue your session.
            </p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleLogout}
            >
              Logout Now
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleStayLoggedIn}
            >
              Stay Logged In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeout;
